import React, { useEffect, useState } from "react";
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import mapValues from 'lodash/mapValues';
import ResetIcon from '@mui/icons-material/SettingsBackupRestoreOutlined';
import Button from '@mui/material/Button';
import groupBy from 'lodash/groupBy';
import mean from "lodash/mean";

export default function ExternalSimulator(props) {
  const {
    controlMeasures, 
    events, 
    impactCategories, 
    scores, 
    bonusPointGroups,
  } = props;

  const [scoredControlMeasures, setScoredControlMeasures] = useState();
  const [score, setScore] = useState();
  const [impactScores, setImpactScores] = useState({});
  const [touched, setTouched] = useState();
  const [scoredBonusPointGroups, setScoredBonusPointGroups] = useState();
  const [riskProfile, setRiskProfile] = useState(50);

  const [eventImpactCategories, setEventImpactCategories] = useState();
  const [controlMeasureEvents, setControlMeasureEvents] = useState();

  useEffect(() => {
    if (events) {
      const _eventImpactCategories = events.map(e => e.impactCategories.split(',').map(cm => ({
        eventId: e.id,
        impactCategoryId: cm
      }))).flat();
      
      setEventImpactCategories(_eventImpactCategories);


      const cmFlat = events.map(e =>
         e.controlMeasures.split(',').filter(x => x.length).map(cm => ({eventId: e.id, controlMeasureId: cm}))
      ).flat();

      const _controlMeasureEvents = mapValues(groupBy(cmFlat, x => x.controlMeasureId), v => v.map(x => x.eventId));
      setControlMeasureEvents(_controlMeasureEvents);
    }
  }, [events]);

  useEffect(() => {
    if (controlMeasures) {
      setScoredControlMeasures(Object.values(controlMeasures).map(e => ({...e, controlStrength: 2.5, na: false})));
    }
  }, [controlMeasures]);

  useEffect(() => {
    setScoredBonusPointGroups(bonusPointGroups);
  }, [bonusPointGroups]);

  useEffect(() => {
    if (scores && scoredControlMeasures && scoredBonusPointGroups && eventImpactCategories && controlMeasureEvents) {
      const _scoredControlMeasures = scoredControlMeasures.filter(cm => !cm.na);
      const controlMeasureScores = _scoredControlMeasures.map(cm => {
        const csIdx = Math.round(cm.controlStrength * 10);
        return {
          controlMeasureId: cm.id,
          score: scores[riskProfile][csIdx]
        };
      })

      const allEventScores = controlMeasureScores.map(cms => 
        controlMeasureEvents[cms.controlMeasureId].map(e => ({eventId: e, score: cms.score}))
      ).flat();

      const eventScoreDict = mapValues(groupBy(allEventScores, x => x.eventId), v => mean(v.map(x => x.score)));

      const impactScores = eventImpactCategories
        .filter(ic => eventScoreDict[ic.eventId] != null)
        .reduce((acc, cur) => {
          const catAcc = acc[cur.impactCategoryId] || {};
          catAcc.total = (catAcc.total || 0) + eventScoreDict[cur.eventId];
          catAcc.count = (catAcc.count || 0) + 1;
          catAcc.avg = catAcc.total / catAcc.count;
          acc[cur.impactCategoryId] = catAcc;
          return acc;
        }, {});

      setImpactScores(impactScores);

      const rawScore = mean(Object.values(eventScoreDict));
      setScore(rawScore);
    }
  }, [scoredControlMeasures, scores, eventImpactCategories, scoredBonusPointGroups, controlMeasureEvents, riskProfile]);

  const scoreLogo = () => {
    if (score > 124)
      return '/Platinum.png';
    else if (score > 99)
      return '/Gold.png';
    else if (score > 74)
      return '/Silver.png';
    else
      return '/Eval.png';
  };

  const handleRiskChange = (e) => {
    setRiskProfile(+e.target.value);
    setTouched(true);
  }

  const handleCSChange = (changeEvent, eventId) => {
    const _scoredControlMeasures = [...scoredControlMeasures];
    _scoredControlMeasures.find(e => e.id === eventId).controlStrength = +changeEvent.target.value;
    setScoredControlMeasures(_scoredControlMeasures);
    setTouched(true);
  }

  const handleNAChange = (changeEvent, eventId) => {
    const _scoredControlMeasures = [...scoredControlMeasures];
    _scoredControlMeasures.find(e => e.id === eventId).na = changeEvent.target.checked;
    setScoredControlMeasures(_scoredControlMeasures);
    setTouched(true);
  }

  const resetDefaults = () => {
    setScoredControlMeasures(Object.values(controlMeasures).map(v => ({...v, controlStrength: 2.5, na: false})));
    setScoredBonusPointGroups(bpg => bpg.map(g => ({...g, selected: ''})));
    setRiskProfile(50);
    setTouched(false);
  }

  return (
    <Paper sx={{height: '100%', backgroundColor: '#104452', backgroundImage: 'none'}}>
      <Stack sx={{height: '100%'}}>
        <Stack 
          direction="row"
          justifyContent="space-between" 
          alignItems="center"
          sx={{width: '800px', margin: '10px auto'}}
        >
          <img src={scoreLogo()} style={{width: '120px', height: '120px'}} alt="" />
          <Typography variant="h2">Score: {score?.toFixed(1)}</Typography>

          <Stack p={1} sx={{width: '180px'}}>
            {Object.entries(impactScores).map(kv => (
              <Stack direction="row" key={`impact-category-${kv[0]}`} justifyContent="space-between">
                <Typography>{impactCategories[+kv[0]]}:</Typography>
                <Typography>{kv[1].avg.toFixed(1)}</Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>

        <Divider />

        <Box flex={1} sx={{overflowY: 'auto'}}>

          <Stack sx={{width: '400px', margin: '8px auto'}}>
            <Typography variant="h6" sx={{textAlign: 'center'}}>Local Risk Profile</Typography>
            <Slider 
              value={riskProfile} 
              onChange={handleRiskChange} 
              valueLabelDisplay="auto"
            />
          </Stack>

          <Divider sx={{width: '800px', margin: '0 auto'}} />

          <Stack sx={{width: '800px', margin: '0 auto'}}>

            <Stack direction="row" py={2} ml={38.5}>
              <Typography variant="h5" sx={{width: '276px'}}>Control Measure</Typography>
              <Typography variant="h5" sx={{width: '230px'}}>Control Strength</Typography>
              {touched && <Button size="small" startIcon={<ResetIcon />} onClick={resetDefaults}>Reset</Button>}
            </Stack>

            <Box marginX="auto">
              {scoredControlMeasures && scoredControlMeasures.map(se => (
                <Stack direction="row" key={se.name} sx={{width: '600px'}} py={0.5}>

                  <Stack direction="row" sx={{minWidth: '280px', width: '280px'}} alignItems="center">

                    <Typography variant="body1" 
                        sx={{
                          lineHeight: '42px', 
                          textDecoration: se.na ? 'line-through' : 'none',
                          color: se.na ? 'text.secondary' : 'text.primary',
                          marginLeft: '8px'
                        }}
                      >
                        {se.name}
                    </Typography>
                    
                  </Stack>

                  <Box flexGrow={1} px={1}>
                    <Slider 
                      min={0}
                      max={5}
                      step={0.1}
                      valueLabelDisplay="auto"
                      value={se.controlStrength} 
                      onChange={e => handleCSChange(e, se.id)} 
                      disabled={se.na}
                    />
                  </Box>
                  
                  <Box px={1}>
                    <FormControlLabel control={<Checkbox checked={se.na} onChange={e => handleNAChange(e, se.id)} />} label="N/A" />
                  </Box>
                </Stack>
              ))}
            </Box>

          </Stack>
        </Box>

      </Stack>
    </Paper>
  )
}